/**
 * Conversion Configuration
 * Single source of truth for all format definitions and tool relationships
 */

export interface FormatDefinition {
  id: string;
  label: string;
  mime: string;
  extensions: string[];
  acceptTypes: string[];
}

export interface ConversionTool {
  from: string;
  to: string;
  slug: string;
  tier: 1 | 2 | 3;
}

/**
 * Format definitions with MIME types and extensions
 */
export const FORMATS: Record<string, FormatDefinition> = {
  jpg: {
    id: 'jpg',
    label: 'JPG',
    mime: 'image/jpeg',
    extensions: ['.jpg', '.jpeg'],
    acceptTypes: ['image/jpeg', 'image/jpg'],
  },
  png: {
    id: 'png',
    label: 'PNG',
    mime: 'image/png',
    extensions: ['.png'],
    acceptTypes: ['image/png'],
  },
  webp: {
    id: 'webp',
    label: 'WebP',
    mime: 'image/webp',
    extensions: ['.webp'],
    acceptTypes: ['image/webp'],
  },
  gif: {
    id: 'gif',
    label: 'GIF',
    mime: 'image/gif',
    extensions: ['.gif'],
    acceptTypes: ['image/gif'],
  },
  bmp: {
    id: 'bmp',
    label: 'BMP',
    mime: 'image/bmp',
    extensions: ['.bmp'],
    acceptTypes: ['image/bmp', 'image/x-bmp'],
  },
};

/**
 * All conversion tool definitions
 */
export const CONVERSIONS: ConversionTool[] = [
  // Tier 1: High-value tools (JPG/PNG/WebP)
  { from: 'jpg', to: 'png', slug: 'jpg-to-png', tier: 1 },
  { from: 'png', to: 'jpg', slug: 'png-to-jpg', tier: 1 },
  { from: 'png', to: 'webp', slug: 'png-to-webp', tier: 1 },
  { from: 'jpg', to: 'webp', slug: 'jpg-to-webp', tier: 1 },
  { from: 'webp', to: 'png', slug: 'webp-to-png', tier: 1 },
  { from: 'webp', to: 'jpg', slug: 'webp-to-jpg', tier: 1 },

  // Tier 2: GIF conversions
  { from: 'gif', to: 'png', slug: 'gif-to-png', tier: 2 },
  { from: 'png', to: 'gif', slug: 'png-to-gif', tier: 2 },
  { from: 'gif', to: 'jpg', slug: 'gif-to-jpg', tier: 2 },
  { from: 'jpg', to: 'gif', slug: 'jpg-to-gif', tier: 2 },
  { from: 'gif', to: 'webp', slug: 'gif-to-webp', tier: 2 },
  { from: 'webp', to: 'gif', slug: 'webp-to-gif', tier: 2 },

  // Tier 3: BMP conversions
  { from: 'bmp', to: 'png', slug: 'bmp-to-png', tier: 3 },
  { from: 'png', to: 'bmp', slug: 'png-to-bmp', tier: 3 },
  { from: 'bmp', to: 'jpg', slug: 'bmp-to-jpg', tier: 3 },
  { from: 'jpg', to: 'bmp', slug: 'jpg-to-bmp', tier: 3 },
  { from: 'bmp', to: 'webp', slug: 'bmp-to-webp', tier: 3 },
  { from: 'webp', to: 'bmp', slug: 'webp-to-bmp', tier: 3 },
  { from: 'bmp', to: 'gif', slug: 'bmp-to-gif', tier: 3 },
  { from: 'gif', to: 'bmp', slug: 'gif-to-bmp', tier: 3 },
];

/**
 * Get format definition by ID
 */
export function getFormat(formatId: string): FormatDefinition | undefined {
  return FORMATS[formatId.toLowerCase()];
}

/**
 * Get conversion tool definition by source and target format
 */
export function getConversion(from: string, to: string): ConversionTool | undefined {
  return CONVERSIONS.find(
    (c) => c.from === from.toLowerCase() && c.to === to.toLowerCase()
  );
}

/**
 * Get related tools for a given conversion (same source OR same target)
 */
export function getRelatedTools(from: string, to: string, limit: number = 6): ConversionTool[] {
  const currentSlug = `${from}-to-${to}`;
  
  return CONVERSIONS
    .filter((c) => {
      // Exclude current tool
      if (c.slug === currentSlug) return false;
      // Include if same source or same target
      return c.from === from || c.to === to;
    })
    .sort((a, b) => a.tier - b.tier) // Sort by tier (higher priority first)
    .slice(0, limit);
}

/**
 * Get all related tools grouped by category
 */
export function getRelatedToolsGrouped(from: string, to: string): {
  sameSource: ConversionTool[];
  sameTarget: ConversionTool[];
} {
  const currentSlug = `${from}-to-${to}`;
  
  const sameSource = CONVERSIONS.filter(
    (c) => c.from === from && c.slug !== currentSlug
  );
  
  const sameTarget = CONVERSIONS.filter(
    (c) => c.to === to && c.slug !== currentSlug
  );
  
  return { sameSource, sameTarget };
}

/**
 * Generate tool title
 */
export function getToolTitle(from: string, to: string): string {
  const fromFormat = getFormat(from);
  const toFormat = getFormat(to);
  return `${fromFormat?.label || from.toUpperCase()} to ${toFormat?.label || to.toUpperCase()} Converter`;
}

/**
 * Generate tool description
 */
export function getToolDescription(from: string, to: string): string {
  const fromFormat = getFormat(from);
  const toFormat = getFormat(to);
  return `Convert ${fromFormat?.label || from.toUpperCase()} images to ${toFormat?.label || to.toUpperCase()} format instantly in your browser. Free, fast, and private — no uploads required.`;
}

/**
 * Get accept string for file input
 */
export function getAcceptString(formatId: string): string {
  const format = getFormat(formatId);
  if (!format) return 'image/*';
  return format.acceptTypes.join(',');
}
